import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * The container everything sits in. Appearance is `.card` in `styles/ui.css`.
 *
 * `padding` defaults to `none` because the console composes cards out of
 * `CardHeader` / `CardBody` / `CardFooter`, which bring their own. The marketing
 * pages have no such sections - they are a heading and a paragraph in a box -
 * and every one of them used to write its own padding, which is how three of
 * them ended up a step apart from each other.
 */

type Tone = "surface" | "sunken" | "panel" | "accent";
type Padding = "none" | "sm" | "md" | "lg";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: Tone;
  padding?: Padding;
  /** Lifts the border and adds a hairline shadow on hover. For clickable cards. */
  interactive?: boolean;
  /** Stretch to the row's tallest card and stack the contents. */
  fill?: boolean;
  /**
   * `li` when the card *is* the list item, rather than sitting inside one.
   * A wrapper `<li>` around a card is a second box the layout has to be told to
   * stretch, which is how a row of cards ends up with mismatched heights.
   */
  as?: "div" | "li";
}

export function Card({
  tone = "surface",
  padding = "none",
  interactive,
  fill,
  as = "div",
  className,
  children,
  ...rest
}: CardProps) {
  // Widened for the same reason as `Reveal`: a "div" | "li" union makes
  // TypeScript intersect both elements' prop types, and the alternative is a
  // generic polymorphic component - a lot of type machinery for one tag swap.
  const Tag = as as React.ElementType;

  return (
    <Tag
      className={cn(
        "card",
        `card--${tone}`,
        padding !== "none" && `card--pad-${padding}`,
        fill && "card--fill",
        interactive && "card--interactive",
        // The panel card is chrome, so anything inside it needs the panel's
        // focus ring and selection colours.
        tone === "panel" && "on-panel",
        className
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function CardHeader({
  icon,
  title,
  subtitle,
  actions,
  className,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("card__header", icon ? "card__header--iconned" : undefined, className)}>
      {icon && <span className="card__icon">{icon}</span>}
      <div className="card__header-text">
        <h3 className="card__title">{title}</h3>
        {subtitle && <p className="card__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="card__actions">{actions}</div>}
    </div>
  );
}

export function CardBody({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={cn("card__body", className)}>{children}</div>;
}

export function CardFooter({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={cn("card__footer", className)}>{children}</div>;
}
